import { STORAGE_KEYS } from '@/constants/storageKeys'
import { getStorage, setStorage } from '@/utils/storage'
import { getTelegramCloudItem, setTelegramCloudItem } from '@/utils/telegramCloudStorage'
import { isTelegramWebAppActive } from '@/utils/telegramWebAppClient'
import logger from '@/utils/logger'

export const LANGUAGE_SOURCE_MANUAL = 'manual'
export const LANGUAGE_SOURCE_KEY = 'preferredLanguageSource'

// English first, then alphabetical by English language name (common i18n menu convention).
export const availableLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '简体中文', flag: '🇨🇳' },
  { code: 'zh-HK', name: '繁體中文', flag: '🇭🇰' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
]

const supportedCodes = availableLanguages.map(lang => lang.code)

export const isSupportedLanguage = (langCode) => supportedCodes.includes(langCode)

export const normalizeLanguageCode = (langCode) => {
  const normalized = String(langCode || '').trim().replace('_', '-').toLowerCase()
  if (!normalized) return ''

  if (normalized.startsWith('zh-hk') || normalized.startsWith('zh-tw') || normalized.startsWith('zh-mo') || normalized.startsWith('zh-hant')) {
    return 'zh-HK'
  }
  if (normalized.startsWith('zh')) return 'zh'

  const baseLang = normalized.split('-')[0]
  return isSupportedLanguage(baseLang) ? baseLang : ''
}

export const getCurrentLanguageName = (locale) => {
  const currentLang = availableLanguages.find(lang => lang.code === locale)
  return currentLang ? currentLang.name : 'English'
}

export const getTelegramLanguage = () => {
  try {
    const webApp = window.Telegram?.WebApp
    return normalizeLanguageCode(webApp?.initDataUnsafe?.user?.language_code)
  } catch (error) {
    logger.debug('Failed to detect Telegram language:', error)
    return ''
  }
}

export const detectBrowserLanguage = () => {
  try {
    return normalizeLanguageCode(navigator.language || navigator.userLanguage || 'en') || 'en'
  } catch (error) {
    logger.warn('Failed to detect browser language:', error)
    return 'en'
  }
}

export const getAutoLanguage = () => {
  return getTelegramLanguage() || detectBrowserLanguage()
}

export const hasManualLanguagePreference = () => {
  const source = getStorage(LANGUAGE_SOURCE_KEY)
  const savedLanguage = getStorage(STORAGE_KEYS.PREFERRED_LANGUAGE)
  return source === LANGUAGE_SOURCE_MANUAL && isSupportedLanguage(savedLanguage)
}

export const getInitialLanguage = () => {
  const savedLanguage = getStorage(STORAGE_KEYS.PREFERRED_LANGUAGE)
  if (hasManualLanguagePreference()) {
    return savedLanguage
  }
  return getAutoLanguage()
}

const toUserProfileLanguage = (langCode) => {
  const normalized = normalizeLanguageCode(langCode)
  if (normalized === 'zh-HK') return 'hk'
  return normalized || 'en'
}

const syncUserLanguagePreference = async (langCode) => {
  const token = getStorage(STORAGE_KEYS.TOKEN)
  const tokenStr = typeof token === 'string' ? token : String(token || '')
  if (!tokenStr.trim() || tokenStr === 'null' || tokenStr === 'undefined') return

  try {
    const { authApi } = await import('@/api/auth')
    await authApi.updateUserLanguage(toUserProfileLanguage(langCode))
  } catch (error) {
    logger.debug('Failed to sync user language preference:', error)
  }
}

export const setManualLanguagePreference = (langCode) => {
  if (!isSupportedLanguage(langCode)) return false
  setStorage(STORAGE_KEYS.PREFERRED_LANGUAGE, langCode)
  setStorage(LANGUAGE_SOURCE_KEY, LANGUAGE_SOURCE_MANUAL)
  if (isTelegramWebAppActive()) {
    void setTelegramCloudItem(STORAGE_KEYS.PREFERRED_LANGUAGE, langCode)
    void setTelegramCloudItem(LANGUAGE_SOURCE_KEY, LANGUAGE_SOURCE_MANUAL)
  }
  void syncUserLanguagePreference(langCode)
  return true
}

export async function hydrateLanguageFromTelegramCloud(localeRef) {
  if (!isTelegramWebAppActive() || !localeRef) return

  try {
    const [savedLanguage, source] = await Promise.all([
      getTelegramCloudItem(STORAGE_KEYS.PREFERRED_LANGUAGE),
      getTelegramCloudItem(LANGUAGE_SOURCE_KEY),
    ])

    if (source === LANGUAGE_SOURCE_MANUAL && isSupportedLanguage(savedLanguage)) {
      localeRef.value = savedLanguage
    }
  } catch (error) {
    logger.debug('Failed to hydrate language from Telegram CloudStorage:', error)
  }
}

export const applyAutoLanguage = (localeRef) => {
  if (hasManualLanguagePreference()) return
  localeRef.value = getAutoLanguage()
}
